// @mui
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
// components
import {
  CircularProgress,
  DialogContent,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useCallback, useEffect, useMemo, useState } from 'react'
import Iconify from 'src/components/iconify'
import { CREATE_ADHOC_MEETING_RECORDER, GET_ACTIVE_MEETING_RECORDERS, SUBSCRIBE_TO_MEETING_RECORDER_STATUS_UPDATES } from 'src/graphql/queries'
import { useMutation, useQuery, useSubscription } from '@apollo/client'
import { useSubscriptionStatus } from 'src/hooks/use-subscription-status'
import { MeetingRecorder } from 'src/__generated__/graphql'
import { isActiveMeetingRecorder } from 'src/components/meeting-recorder-controls/util'
import { MeetingTransportControls } from 'src/components/meeting-recorder-controls/meeting-recorder-controls'
import { getValidMeetingLinkType } from 'src/utils/validation'
import FreeTrialEndedBanner from './free-trial-ended-banner'

// ----------------------------------------------------------------------

type Props = {
  open: boolean
  onClose: VoidFunction
}

export default function MeetingBotControlsDialog({ open, onClose }: Props) {
  const { subscribed } = useSubscriptionStatus()
  const [activeAdHocMeetingRecorders, setActiveAdHocMeetingRecorders] = useState<MeetingRecorder[]>([])
  const { data: activeMeetingRecorders } = useQuery(GET_ACTIVE_MEETING_RECORDERS, {
    fetchPolicy: 'cache-and-network',
    skip: !open,
    onCompleted: (data) => {
      setActiveAdHocMeetingRecorders(
        data.getActiveMeetingRecorders.filter((mr) => !mr.meetingId && isActiveMeetingRecorder(mr))
      )
    },
  })
  useSubscription(SUBSCRIBE_TO_MEETING_RECORDER_STATUS_UPDATES, {
    skip: !open,
    onData: ({data}) => {
      const update = data.data?.meetingRecorderUpdates
      if (!update) {
        return
      }
      setActiveAdHocMeetingRecorders((prev) => {
        const newList = prev.map((mr) => (mr.id === update.meetingRecorderId ? { ...mr, status: update.status } : mr))
        // if the meeting recorder is no longer active, remove it from the list
        return newList.filter((mr) => isActiveMeetingRecorder(mr))
      })
    },
  })

  useEffect(() => {
    if (activeMeetingRecorders) {
      setActiveAdHocMeetingRecorders(
        activeMeetingRecorders.getActiveMeetingRecorders.filter((mr) => !mr.meetingId && isActiveMeetingRecorder(mr))
      )
    }
  }, [activeMeetingRecorders])

  const renderActiveAdHocRecorders = useMemo(
    () => (
      <Stack spacing={1}>
        {activeAdHocMeetingRecorders.map((mr, index) => (
          <Paper key={index} sx={{ p: { xs: 1, sm: 2 },  backgroundColor: 'background.neutral' }}>
            <Stack direction="column" spacing={2}>
              <Stack direction="row" justifyContent="stretch" alignItems="center" spacing={1}>
                <Typography variant="body2">
                  Meeting Url
                </Typography>
                <TextField variant="outlined" size="small" sx={{ flexGrow: 1 }} value={mr.meetingUrl} disabled />
              </Stack>
              <MeetingTransportControls status={mr.status} meetingRecorderId={mr.id} />
            </Stack>
          </Paper>
        ))}
      </Stack>
    ),
    [activeAdHocMeetingRecorders]
  )

  const handleAdhocMeetingRecorderCreated = useCallback((meetingRecorder: MeetingRecorder) => {
    setActiveAdHocMeetingRecorders((prev) => {
      if (prev.some((mr) => mr.id === meetingRecorder.id)) {
        return prev
      }
      return [...prev, meetingRecorder]
    })
  }, [])

  const inviteToMeetingWithUrlControls = useMemo(
    () => (
      <InviteToMeetingWithUrlControls
        onAdhocMeetingRecorderCreated={handleAdhocMeetingRecorderCreated}
        disabled={!subscribed}
      />
    ),
    [subscribed, handleAdhocMeetingRecorderCreated]
  )

  const meetingRecorderIsActive = useMemo(() => activeAdHocMeetingRecorders.length > 0, [activeAdHocMeetingRecorders])

  return (
    <Dialog
      PaperProps={{
        sx: (theme) => ({
          maxWidth: theme.breakpoints.values.md,
          width: theme.breakpoints.values.sm,
          overflow: 'auto',
          minHeight: 300,
        }),
      }}
      sx={{ overflow: 'auto' }}
      open={open}
      onClose={onClose}
    >
      <DialogActions sx={(theme) => ({ p: 2, minHeight: 0, background: theme.palette.background.neutral })}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          Invite Ambient to your meeting
        </Typography>
        <Button variant="text" sx={{ minWidth: 0 }} onClick={onClose}>
          Close
        </Button>
      </DialogActions>
      {!subscribed && (
        <Stack mx={1} mt={1}>
          <FreeTrialEndedBanner />
        </Stack>
      )}
      <DialogContent sx={{ p: { xs: 1, sm: 2 } }}>
        <Stack spacing={2} width={1}>
          <Typography variant="body1" p={1}>
            {meetingRecorderIsActive
              ? `Your meeting recorder is active. Use the controls below to pause or stop recording.`
              : `Enter a meeting link below and we'll automatically record, transcribe, and summarize your meeting.`}
          </Typography>
          {!meetingRecorderIsActive && (
            <Stack direction="row" spacing={2} justifyContent="center">
              <Typography component="div" variant="caption" sx={{ my: 0.5, color: 'text.secondary' }}>
                Supported Platforms
              </Typography>
              <Iconify icon="logos:zoom" width={45} mt={-1} />
              <Iconify icon="logos:microsoft-teams" width={26} />
              <Iconify icon="logos:google-meet" width={26} />
            </Stack>
          )}
          {/* {renderForm} */}
          {/* {!showForm.value && renderControls} */}
          {activeAdHocMeetingRecorders.length > 0 ? renderActiveAdHocRecorders : inviteToMeetingWithUrlControls}
        </Stack>
      </DialogContent>
    </Dialog>
  )
}

function InviteToMeetingWithUrlControls({
  onAdhocMeetingRecorderCreated,
  disabled,
}: {
  onAdhocMeetingRecorderCreated: (meetingRecorder: MeetingRecorder) => void
  disabled?: boolean
}) {
  const [error, setError] = useState<string | null>(null)
  const [meetingUrl, setMeetingUrl] = useState<string>('')
  const [loading, setLoading] = useState(false)
  const [createAdhocMeetingRecorder] = useMutation(CREATE_ADHOC_MEETING_RECORDER)
  const handleStartRecording = useCallback(async () => {
    if (!getValidMeetingLinkType(meetingUrl)) {
      setError('Invalid URL, please enter a valid meeting link')
      return
    }
    setLoading(true)
    if (!meetingUrl) {
      return
    }
    try {
      const result = await createAdhocMeetingRecorder({
        variables: {
          meetingUrl,
        },
        refetchQueries: [GET_ACTIVE_MEETING_RECORDERS],
      })
      if (result.data?.createAdHocMeetingRecorder) {
        onAdhocMeetingRecorderCreated(result.data.createAdHocMeetingRecorder)
      }
    } catch (e) {
      console.error(e)
      setError('Failed to create meeting recorder, please try again')
      setLoading(false)
    }
  }, [createAdhocMeetingRecorder, meetingUrl, onAdhocMeetingRecorderCreated])

  useEffect(() => {
    setMeetingUrl('')
    setError(null)
  }, [disabled])

  return (
    <Stack direction="row" width={1} spacing={2} justifyContent="space-between" alignItems="center">
      <TextField
        name="meetingUrl"
        label="Meeting URL"
        autoFocus
        disabled={loading}
        value={meetingUrl}
        onChange={(e) => {
          setMeetingUrl(e.target.value)
          setError(null)
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            handleStartRecording()
          }
        }}
        fullWidth
        error={!!error}
        helperText={error}
        InputProps={{
          placeholder: 'https://zoom.us/j/1234567890',
          endAdornment: (
            <InputAdornment position="end">
              <Button variant="contained" color="primary" disabled={loading || disabled} onClick={handleStartRecording}>
                {loading ? <CircularProgress size={20} /> : 'Invite'}
              </Button>
            </InputAdornment>
          ),
        }}
      />
    </Stack>
  )
}
